const sensitiveDataKeys = {
    bridgeName: true,
    location: true,
    subject: true,
    description: true,
    msg: true,
    discovery: true,
    connectedCalendarIds: true,
    disconnectedCalendarIds: true,
    calendarId: true,
    ipAddresses: true,
    syncTokens: true,
};
const lengthPreservedKeys = {
    displayName: true,
    firstName: true,
    lastName: true,
    pronouncedName: true,
    meetingName: true,
    password: true,
    _password_: true,
    meetingPassword: true,
    _meetingPassword_: true,
    meetingPasswordMasked: true,
    _meetingPasswordMasked_: true,
    meetingPasswordPSTN: true,
    _meetingPasswordPSTN_: true,
    access_token: true,
    refresh_token: true,
    token: true,
    _token_: true,
    notificationToken: true,
    _notificationToken_: true,
    authKey: true,
    _authKey_: true,
    pstn: true,
    _pstn_: true,
    notificationSubKey: true,
    _notificationSubKey_: true,
    subscribeKey: true,
    _subscribeKey_: true,
};

/**
 * Replaces a string with its initials, preserving the length of each word.
 * @param {string} str - The string to be replaced.
 * @returns {string} The replaced string.
 * @example
 * replaceWithInitials('John Doe'); // returns 'J*** D**'
 * replaceWithInitials('Jane'); // returns 'J***'
 */
export function replaceWithInitials(str: string): string {
    if (!str) return str;

    const splitName = str.trim().split(/\s+/);
    const resultParts: string[] = [];

    for (let i = 0; i < splitName.length; i++) {
        const namePart = splitName[i];
        if (namePart.length === 0) continue;

        resultParts.push(namePart[0] + '*'.repeat(namePart.length - 1));
    }

    return resultParts.join(' ');
}

/**
 * Obscures an email address by replacing the middle characters with asterisks.
 * @param {string} str - The email address to be obscured.
 * @returns {string} The obscured email address.
 * @example
 * obscureEmail('john.doe@example.com'); // returns 'joh*****@example.com'
 * obscureEmail('joe@example.com'); // returns 'j**@example.com'
 */
export function obscureEmail(str: string): string {
    if (!str) return str;

    const atIndex = str.indexOf('@');

    if (atIndex === -1) return str;

    const name = str.substring(0, atIndex);
    const domain = str.substring(atIndex + 1);

    const namePart = name.length <= 5 ? name.substring(0, 1) : name.substring(0, 3);
    const obscuredPart = '*'.repeat(name.length - namePart.length);

    return `${namePart}${obscuredPart}@${domain}`;
}

/**
 * Replaces a string with asterisks of the same length.
 * @param {string} str - The string to be replaced.
 * @returns {string} A string of asterisks of the same length.
 */
export function maskString(str: string): string {
    if (!str) return str;

    return '*'.repeat(str.length);
}

type BreakoutRoom = {
    participants?: Record<string, { name?: string; email?: string }>;
};

function processBreakoutRoomsSensitiveData(value: BreakoutRoom): BreakoutRoom {
    const newValue = { ...value };
    const rooms = Object.keys(newValue);

    for (const roomId of rooms) {
        const room = newValue[roomId];

        if (room?.participants) {
            const participantIds = Object.keys(room.participants);

            for (const participantId of participantIds) {
                if (room.participants[participantId].name) {
                    room.participants[participantId].name = replaceWithInitials(
                        room.participants[participantId].name
                    );
                }

                if (room.participants[participantId].email) {
                    room.participants[participantId].email = obscureEmail(
                        room.participants[participantId].email
                    );
                }
            }
        }
    }

    return newValue;
}

type CISSensitiveData = {
    calendars?: Record<string, unknown>;
    events?: Record<string, unknown>;
};

function processCISSensitiveData<T extends CISSensitiveData>(value: T): T {
    const newValue: T = { ...value };

    if (newValue?.calendars) {
        const calendarsKeys = Object.keys(newValue.calendars);

        for (const calendar of calendarsKeys) {
            if (Array.isArray(newValue.calendars[calendar])) {
                newValue.calendars[calendar] = (
                    newValue.calendars[calendar] as Record<string, any>[]
                ).map(item => ({
                    ...item,
                    name: replaceWithInitials(item.name),
                    id: obscureEmail(item.id),
                }));
            }
        }
    }

    if (newValue?.events) {
        const eventsKeys = Object.keys(newValue.events);

        for (const eventKey of eventsKeys) {
            newValue.events[obscureEmail(eventKey)] = newValue.events[eventKey];
            delete newValue.events[eventKey];
        }
    }

    return removeSensitiveData(newValue);
}

type KeyProcessor<T = unknown> = (value: T) => T;
type KeyProcessors = {
    email: KeyProcessor<string>;
    calendarIntegrationService: KeyProcessor<CISSensitiveData>;
    rct: KeyProcessor<{ bridge: { name: string } }>;
    cellsGrid: KeyProcessor<{ cells: Array<{ name: string }> }>;
    breakoutRooms: KeyProcessor<{ actualLayout?: BreakoutRoom; userLayout?: BreakoutRoom }>;
    attendees: KeyProcessor<Array<{ name: string; email: string }>>;
    pmiCache: KeyProcessor<{ name: string }>;
    personalMeeting: KeyProcessor<{ name: string }>;
    organizer: KeyProcessor<{ name: string; email: string }>;
    avatarUrl: KeyProcessor<string>;
};

const keyProcessors: KeyProcessors = {
    email: obscureEmail,
    calendarIntegrationService: processCISSensitiveData,
    rct: value => {
        if (!value || !value.bridge) return value;

        return {
            ...value,
            bridge: {
                ...value.bridge,
                name: replaceWithInitials(value.bridge.name),
            },
        };
    },
    cellsGrid: value => {
        if (!value || !Array.isArray(value.cells)) return value;

        return {
            ...value,
            cells: value.cells.map(cell => ({
                ...cell,
                name: cell?.name ? replaceWithInitials(cell.name) : cell.name,
            })),
        };
    },
    breakoutRooms: value => ({
        ...value,
        actualLayout: value?.actualLayout
            ? processBreakoutRoomsSensitiveData(value.actualLayout)
            : undefined,
        userLayout: value?.userLayout
            ? processBreakoutRoomsSensitiveData(value.userLayout)
            : undefined,
    }),
    attendees: value => {
        if (!Array.isArray(value)) return value;

        return value.map(attendee => ({
            ...attendee,
            name: attendee?.name ? replaceWithInitials(attendee.name) : attendee.name,
            email: attendee?.email ? obscureEmail(attendee.email) : attendee.email,
        }));
    },
    pmiCache: value => {
        if (!value || !value.name) return value;

        return { ...value, name: replaceWithInitials(value.name) };
    },
    personalMeeting: value => {
        if (!value || !value.name) return value;

        return { ...value, name: replaceWithInitials(value.name) };
    },
    organizer: value => {
        if (!value) return value;

        return {
            ...value,
            name: value.name ? replaceWithInitials(value.name) : value.name,
            email: value.email ? obscureEmail(value.email) : value.email,
        };
    },
    avatarUrl: value => {
        try {
            const originLength = window.location.origin.length;
            const url = new URL(value, window.location.origin);
            const searchParams = url.searchParams;

            for (const paramKey of searchParams.keys()) {
                const paramValue = searchParams.get(paramKey) || '';

                if (sensitiveDataKeys[paramKey]) {
                    searchParams.set(paramKey, 'Removed by PII filter');
                } else if (lengthPreservedKeys[paramKey]) {
                    searchParams.set(paramKey, maskString(paramValue));
                } else if (keyProcessors[paramKey]) {
                    const processor = keyProcessors[paramKey];
                    searchParams.set(paramKey, paramValue ? processor(paramValue) : paramValue);
                }
            }

            return url.href.substring(originLength);
        } catch (e) {
            return value;
        }
    },
};

const getTypeTag = (value: unknown) => Object.prototype.toString.call(value);

type SensitiveData = Record<string, unknown> | Array<any> | Set<any> | Map<any, any> | Error;

const removeSensitiveDataTypeHandlers: Record<
    string,
    (obj: any, replaceKeys: boolean, seen: WeakSet<SensitiveData>) => any
> = {
    '[object Array]': (
        obj: Array<any>,
        replaceKeys: boolean,
        seen: WeakSet<SensitiveData>
    ): Array<any> => {
        const newArray: Array<any> = new Array(obj.length);

        for (let i = 0; i < obj.length; i++) {
            newArray[i] = removeSensitiveData(obj[i], replaceKeys, seen);
        }

        return newArray;
    },
    '[object Set]': (
        obj: Set<any>,
        replaceKeys: boolean,
        seen: WeakSet<SensitiveData>
    ): Set<any> => {
        const newSet = new Set() as typeof obj;

        for (const item of obj) {
            newSet.add(removeSensitiveData(item, replaceKeys, seen));
        }

        return newSet;
    },
    '[object Map]': (
        obj: Map<any, any>,
        replaceKeys: boolean,
        seen: WeakSet<SensitiveData>
    ): Map<any, any> => {
        const newMap = new Map() as typeof obj;

        for (const [key, value] of obj) {
            const newKey = removeSensitiveData(key, replaceKeys, seen);
            const newValue = removeSensitiveData(value, replaceKeys, seen);
            newMap.set(newKey, newValue);
        }

        return newMap;
    },
    '[object Error]': (obj: Error, replaceKeys: boolean, seen: WeakSet<SensitiveData>): Error => {
        const Constructor = obj.constructor as { new (message?: string): Error };
        const newError = new Constructor(removeSensitiveData(obj.message, replaceKeys, seen));
        const props = Object.getOwnPropertyNames(obj);

        for (let i = 0; i < props.length; i++) {
            const prop = props[i];
            if (prop !== 'message' && prop !== 'stack') {
                newError[prop] = removeSensitiveData(obj[prop], replaceKeys, seen);
            }
        }

        return newError;
    },
    '[object Object]': (
        obj: Record<string, any>,
        replaceKeys: boolean,
        seen: WeakSet<SensitiveData>
    ): Record<string, any> => {
        const proto = Object.getPrototypeOf(obj);
        const result = Object.create(proto);
        const keys = Object.keys(obj);

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = obj[key];

            if (sensitiveDataKeys[key]) {
                if (replaceKeys) {
                    result[key] = 'Removed by PII filter';
                }
            } else if (lengthPreservedKeys[key] && typeof value === 'string') {
                result[key] = maskString(value);
            } else {
                const processor = keyProcessors[key];

                if (processor) {
                    result[key] = processor(value);
                } else {
                    result[key] = removeSensitiveData(value, replaceKeys, seen);
                }
            }
        }

        return result;
    },
};

/**
 * Removes sensitive data from the given collection.
 */
export function removeSensitiveData<T extends unknown>(
    obj: T,
    replaceKeys = true,
    seen = new WeakSet()
): T {
    if (!obj || typeof obj !== 'object') {
        return obj;
    }

    if (seen.has(obj as SensitiveData)) {
        return obj;
    }

    seen.add(obj as SensitiveData);

    const typeTag = getTypeTag(obj) as keyof typeof removeSensitiveDataTypeHandlers;
    const handler = removeSensitiveDataTypeHandlers[typeTag];

    if (handler) {
        return handler(obj, replaceKeys, seen);
    }

    return obj;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const warn = process.env.NODE_ENV === 'development' ? console.warn : () => {};

/**
 * Detects sensitive data in the given object.
 */
export function detectSensitiveData<T extends unknown>(
    obj: T,
    path = '',
    seen = new WeakSet()
): boolean {
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    if (seen.has(obj as SensitiveData)) {
        return false;
    }

    seen.add(obj as SensitiveData);

    const typeTag = getTypeTag(obj);

    if (removeSensitiveDataTypeHandlers[typeTag]) {
        const keys = Object.keys(obj as Record<string, unknown>);

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const currentPath = path ? `${path}.${key}` : key;

            if (sensitiveDataKeys[key]) {
                warn(`Sensitive data to be removed detected at path: ${currentPath}`);
                return true;
            }

            if (lengthPreservedKeys[key]) {
                warn(`Sensitive data to be masked detected at path: ${currentPath}`);
                return true;
            }

            if (keyProcessors[key]) {
                warn(`Sensitive data to be processed detected at path: ${currentPath}`);
                return true;
            }

            if (detectSensitiveData(obj[key], currentPath, seen)) {
                return true;
            }
        }
    }

    return false;
}
